import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../../Api';
import ConfirmationModal from './components/ConfirmationModal';
import AlertModal from './components/AlertModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faEye, faTrash, faSearch, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

const QuotaProjects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  // Recherche/filtre/page vivent dans l'URL : revenir depuis la fiche d'un projet (bouton
  // Retour ou retour navigateur) rebâtit la liste telle qu'elle était, au lieu de tout perdre.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || '';
  const currentPage = Math.max(1, Number(searchParams.get('page')) || 1);

  /** Écrit dans l'URL ; une valeur vide (ou page 1) retire le paramètre pour garder l'URL propre. */
  const updateParams = (next) => {
    const p = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      const v = typeof value === 'function' ? value(currentPage) : value;
      if (v === '' || v === null || v === undefined || (key === 'page' && Number(v) === 1)) p.delete(key);
      else p.set(key, String(v));
    });
    // replace: pas d'entrée d'historique par filtre, sinon Retour repasse par chaque frappe.
    setSearchParams(p, { replace: true });
  };
  const setStatusFilter = (value) => updateParams({ status: value, page: 1 });
  const setCurrentPage = (value) => updateParams({ page: value });

  // searchInput = ce qui est tapé ; searchTerm (URL) = ce qui est appliqué, sur Entrée / bouton.
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, currentPage]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, per_page: PAGE_SIZE };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;

      const response = await axios.get('/quotas/projects', { params });
      setProjects(response.data.data || []);
      setTotalPages(response.data.last_page || 1);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const applySearch = (e) => {
    if (e) e.preventDefault();
    updateParams({ search: searchInput.trim(), page: 1 });
  };

  const clearSearch = () => {
    setSearchInput('');
    updateParams({ search: '', page: 1 });
  };

  const handleDelete = (projectId) => {
    setDeleteProjectId(projectId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/quotas/projects/${deleteProjectId}`);
      setProjects(projects.filter(p => p.id !== deleteProjectId));
      setShowDeleteConfirm(false);
      setDeleteProjectId(null);
    } catch (error) {
      console.error('Error deleting project:', error);
      setShowDeleteConfirm(false);
      setDeleteProjectId(null);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: 'Erreur lors de la suppression',
        type: 'error'
      });
    }
  };

  const getStatusBadgeClass = (status) => {
    const baseClasses = 'px-3 py-1 rounded-full text-xs font-semibold bg-white whitespace-nowrap';
    switch (status) {
      case 'draft':
        return `${baseClasses} text-yellow-700`;
      case 'in_progress':
        return `${baseClasses} text-blue-600`;
      case 'review':
        return `${baseClasses} text-gray-600`;
      case 'locked':
        return `${baseClasses} text-red-600`;
      case 'finished':
        return `${baseClasses} text-green-600`;
      default:
        return `${baseClasses} text-gray-600`;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft':
        return 'Brouillon';
      case 'in_progress':
        return 'En cours';
      case 'review':
        return 'En révision';
      case 'locked':
        return 'Verrouillé';
      case 'finished':
        return 'Terminé';
      default:
        return status;
    }
  };

  // Full-page spinner only on the very first load — later fetches (search, filter, paging)
  // keep the page mounted so the search field doesn't lose its text/focus.
  if (loading && initialLoad) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-red-600 mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Quotas</h1>
        {!['administrateur_general', 'administrateur_general_superieur'].includes(user?.role) && (
          <button
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
            onClick={() => navigate('/quotas/create')}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Nouveau Quota</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
        {/* Search runs on Enter or on the search button — not on every keystroke */}
        <form onSubmit={applySearch} className="relative flex-1 max-w-full sm:max-w-md">
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-4 pr-20 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
          />
          {(searchInput || searchTerm) && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-10 top-1/2 transform -translate-y-1/2 px-2 text-gray-400 hover:text-gray-600"
              title="Effacer la recherche"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 text-gray-500 hover:text-red-600"
            title="Rechercher"
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none min-w-[200px]"
        >
          <option value="">Tous les statuts</option>
          <option value="draft">Brouillon</option>
          <option value="locked">Verrouillé</option>
          <option value="finished">Terminé</option>
        </select>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {loading ? (
          <div className="col-span-full text-center py-12 sm:py-16">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-red-600 mb-3" />
            <p className="text-gray-600">Chargement...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="col-span-full text-center py-12 sm:py-16">
            <p className="text-gray-500 text-lg">Aucun projet de quota trouvé</p>
          </div>
        ) : (
          [...projects].sort((a, b) => (a.status === 'finished' ? 1 : 0) - (b.status === 'finished' ? 1 : 0)).map((project, idx, sorted) => (
            <React.Fragment key={project.id}>
              {project.status === 'finished' && idx > 0 && sorted[idx - 1]?.status !== 'finished' && (
                <div className="col-span-full border-t border-gray-200 pt-4 mt-2 mb-0">
                  <p className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Projets terminés</p>
                </div>
              )}
            <div
              className={`border rounded-lg p-4 sm:p-5 transition-shadow ${project.status === 'finished' ? 'bg-gray-100 border-white opacity-60' : 'bg-white border-gray-200 shadow-sm hover:shadow-md'}`}
              style={project.status === 'finished' ? { boxShadow: '0 0 0 2px white, 0 1px 3px rgba(0,0,0,0.06)' } : undefined}
            >
              {/* Project Header */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex-1 min-w-0 break-words" style={{ overflowWrap: 'anywhere' }}>
                  {project.survey?.title || `Projet #${project.id}`}
                </h3>
                <span className={`${getStatusBadgeClass(project.status)} flex-shrink-0 whitespace-nowrap`}>
                  {getStatusLabel(project.status)}
                </span>
              </div>

              {/* Vague (wave) badge — differentiates quotas of the same tracker survey */}
              {project.survey_wave?.label && (
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                    {project.survey_wave.label}
                  </span>
                  {(project.survey_wave.date_debut || project.survey_wave.date_fin) && (
                    <span className="text-xs text-gray-500">
                      {project.survey_wave.date_debut
                        ? new Date(project.survey_wave.date_debut).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : '?'}
                      {' → '}
                      {project.survey_wave.date_fin
                        ? new Date(project.survey_wave.date_fin).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : '?'}
                    </span>
                  )}
                </div>
              )}

              {/* Survey Info */}
              {project.survey && (
                <div className="text-sm text-gray-600 mb-4 pb-4 border-b border-gray-100">
                  {project.survey.title}
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-4">
                <div className="flex flex-col">
                  <label className="text-xs text-gray-600 mb-1">Objectif</label>
                  <span className="text-base font-semibold text-gray-900">
                    {project.overall_target?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-gray-600 mb-1">Alloué</label>
                  <span className="text-base font-semibold text-gray-900">
                    {project.total_allocated?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-gray-600 mb-1">% Alloué</label>
                  <span className="text-base font-semibold text-gray-900">
                    {project.percentage_allocated?.toFixed(1) || 0}%
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end pt-4 border-t border-gray-100">
                <button
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                  onClick={() => navigate(`/quotas/${project.id}`)}
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span className="hidden sm:inline">Ouvrir</span>
                </button>
                {project.status === 'draft' && !['administrateur_general', 'administrateur_general_superieur'].includes(user?.role) && (
                  <button
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center transition-colors"
                    onClick={() => handleDelete(project.id)}
                    title="Supprimer"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            </div>
            </React.Fragment>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            Précédent
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setCurrentPage(p)}
              className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
                p === currentPage
                  ? 'bg-red-600 border-red-600 text-white'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            Suivant
          </button>
        </div>
      )}

      <ConfirmationModal
        show={showDeleteConfirm}
        title="Supprimer le projet"
        message="Êtes-vous sûr de vouloir supprimer ce projet ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteProjectId(null);
        }}
      />

      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />
    </div>
  );
};

export default QuotaProjects;

